// ─── MILESTONE / STREAK DETECTORS ───────────────────────────────────────
// Pure functions of the innings ball-log. They all work from inn.ballLog so they
// survive page reload and undo correctly, and have no app-state/DOM dependencies.

// Walk back from end of ballLog until we hit a wicket (or start of innings).
// Returns per-player run/ball contributions + partnership extras + total.
export function getCurrentPartnership(inn, matchRules = {}) {
  const lastWicketIndex = inn.ballLog.findLastIndex(b => b.type === "wicket");
  const slice = inn.ballLog.slice(lastWicketIndex + 1);

  const players = {};
  const s1 = inn.currentStriker, s2 = inn.currentNonStriker;
  if (s1) players[s1] = { runs: 0, balls: 0 };
  if (s2) players[s2] = { runs: 0, balls: 0 };

  const nbVal = matchRules && matchRules.noBallValue !== undefined ? matchRules.noBallValue : 1;
  let extras = 0, totalRuns = 0, totalBalls = 0;
  for (const b of slice) {
    const s = b.striker;
    if (!players[s]) players[s] = { runs: 0, balls: 0 };

    if (b.type === "wide") {
      extras += b.runs;
    } else if (b.type === "nb") {
      if (b.runSource === "b" || b.runSource === "lb") {
        extras += b.runs;
      } else {
        extras += nbVal;
        players[s].runs += Math.max(0, b.runs - nbVal);
      }
      players[s].balls += 1;
    } else if (b.type === "b" || b.type === "lb") {
      extras += b.runs;
      players[s].balls += 1;
      totalBalls += 1;
    } else if (b.type === "run" || b.type === "dot") {
      players[s].runs += b.runs;
      players[s].balls += 1;
      totalBalls += 1;
    }
    totalRuns += b.runs;
  }
  return { players, extras, totalRuns, totalBalls };
}

// How many consecutive legal balls FACED by this batsman were sixes.
// Wides/nbs are skipped (not faced). A legal ball faced by the OTHER batsman
// breaks the streak — this is what makes it "Yuvraj-style" rather than just
// "this batsman has hit N sixes anywhere this innings".
export function consecutiveSixesByStriker(inn, batsmanName) {
  let count = 0;
  for (let i = inn.ballLog.length - 1; i >= 0; i--) {
    const b = inn.ballLog[i];
    if (b.striker !== batsmanName) break; // strike rotated away → streak broken
    if (b.type === 'wide') continue; // wides aren't "balls faced", but NBs can be hit for 6
    if (b.type === "run" && b.runs === 6) { count++; continue; }
    break; // any other legal ball faced (dot, run<6, b/lb, wicket) breaks the streak
  }
  return count;
}

// Classify the bowler's last few legal balls.
// "achieved" = 3+ consecutive legal-ball wickets (excluding run-outs since
// those aren't credited to the bowler for hat-trick purposes).
// "pending"  = exactly 2 consecutive bowler-credited wickets, no legal ball after.
export function bowlerHatTrickStatus(inn, bowlerName) {
  let streak = 0;
  for (let i = inn.ballLog.length - 1; i >= 0; i--) {
    const b = inn.ballLog[i];
    if (b.bowler !== bowlerName) continue;
    if (!b.isLegal && b.type !== 'wicket') continue;
    if (b.type === "wicket" && b.wicketType !== "RunOut") {
      streak++;
      continue;
    }
    break;
  }
  if (streak >= 3) return "achieved";
  if (streak === 2) return "pending";
  return "none";
}

// Returns "golden" | "diamond" | "regular" | null based on the dismissed batsman's
// post-dismissal stats. Call AFTER recordWicket so balls/runs reflect this ball.
// (Duck type depends only on balls faced, not the dismissal type — the second
// arg is kept for call-site symmetry with other stat helpers.)
export function duckTypeFor(outBat, _wicketType) {
  if (!outBat || outBat.runs !== 0) return null;
  // Out without facing a single ball (run out backing up, or stumped/hit-wicket
  // off a wide) — a diamond duck regardless of dismissal type.
  if (outBat.balls === 0) return "diamond";
  if (outBat.balls === 1) return "golden";
  return "regular";
}
